import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

const Section = styled.section`
  padding: 120px 2rem;
  background: #0A0A0A;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 80px 1.25rem; }
`;

const BgGlow = styled.div`
  position: absolute;
  left: -200px;
  top: 50%;
  transform: translateY(-50%);
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, rgba(244,121,32,0.06) 0%, transparent 60%);
  pointer-events: none;
`;

const SideImg = styled(motion.img)`
  position: absolute;
  width: 200px;
  border-radius: 14px;
  object-fit: cover;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  pointer-events: none;

  @media (max-width: 1100px) { width: 150px; }
  @media (max-width: 768px)  { width: 100px; }
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 6rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

/* ── Columna izquierda ── */
const Left = styled.div`
  position: sticky;
  top: 8rem;

  @media (max-width: 900px) {
    position: static;
  }
`;

const EyebrowWrap = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Line = styled.div`
  width: 32px;
  height: 2px;
  background: #F47920;
  border-radius: 1px;
  flex-shrink: 0;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 600;
`;

const Title = styled(motion.h2)`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(2.8rem, 5vw, 5.5rem);
  font-weight: 900;
  text-transform: uppercase;
  line-height: 0.9;
  letter-spacing: -0.02em;
  margin: 0 0 1.5rem;
`;

const Accent = styled.span`
  color: #F47920;
`;

const Sub = styled(motion.p)`
  font-size: 0.95rem;
  color: #fff;
  line-height: 1.75;
  max-width: 280px;

  @media (max-width: 900px) { max-width: 100%; }
`;

const OLine = styled(motion.div)`
  height: 3px;
  width: 48px;
  background: #F47920;
  border-radius: 2px;
  margin-top: 2rem;
`;

/* ── Columna derecha ── */
const Right = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContactRow = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.6rem 1.2rem;
  border-bottom: 1px solid #161616;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: background 0.2s ease;
  border-radius: 8px;

  &:first-child { border-top: 1px solid #161616; }

  &:hover {
    background: rgba(244, 121, 32, 0.04);
  }

  &:hover .row-arrow {
    opacity: 1;
    transform: translateX(0);
    color: #F47920;
  }

  &:hover .row-icon {
    background: rgba(244, 121, 32, 0.18);
    border-color: rgba(244, 121, 32, 0.35);
    color: #F47920;
  }
`;

const RowIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: #111;
  border: 1px solid #1E1E1E;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  svg { width: 20px; height: 20px; }
`;

const RowContent = styled.div`
  flex: 1;
  min-width: 0;

  .label {
    font-size: 0.68rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  .value {
    font-size: 1rem;
    color: #fff;
    font-weight: 500;
    line-height: 1.5;
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;

const RowArrow = styled.div`
  color: #333;
  opacity: 0.4;
  transform: translateX(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease, color 0.2s ease;
  flex-shrink: 0;

  svg { width: 18px; height: 18px; }
`;

/* ── WhatsApp strip ── */
const WAStrip = styled(motion.div)`
  margin-top: 3rem;
  background: linear-gradient(135deg, #0d2e1a 0%, #0a2116 100%);
  border: 1px solid rgba(37,211,102,0.3);
  box-shadow: inset 0 1px 0 rgba(37,211,102,0.12);
  border-radius: 16px;
  padding: 1.75rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }
`;

const WAText = styled.div`
  .headline {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    text-transform: uppercase;
    margin-bottom: 0.2rem;
  }
  .sub { font-size: 0.85rem; color: #4a7c5e; }
`;

const WABtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: #25D366;
  color: #fff;
  padding: 0.85rem 2rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover { background: #1ebe5d; }

  svg { width: 20px; height: 20px; }
`;

const WA_LINK = 'https://wa.me/523951145320?text=Hola%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20Carnes%20Fereima.';
const MAPS_LINK = 'https://maps.google.com/?q=Rancho+los+Aguirre+2,+San+Juan+de+los+Lagos,+Jalisco';

const EASE = [0.22, 1, 0.36, 1];

const ROWS = [
  {
    label: 'Teléfono',
    value: '395 114 53 20',
    href: 'tel:3951145320',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
      </svg>
    ),
  },
  {
    label: 'WhatsApp',
    value: '395 114 53 20',
    href: WA_LINK,
    target: '_blank',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.124 1.532 5.862L0 24l6.337-1.501A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.584-.5-5.08-1.374l-.362-.214-3.763.891.936-3.667-.237-.375A9.954 9.954 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
      </svg>
    ),
  },
  {
    label: 'Correo Electrónico',
    value: 'administracion@carnesfereima.com\ncarnesfereima@gmail.com',
    href: 'mailto:administracion@carnesfereima.com',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
      </svg>
    ),
  },
  {
    label: 'Horario de Atención',
    value: 'Lunes a Viernes: 7:00 AM — 4:00 PM\nSábado: 7:00 AM — 2:00 PM',
    href: null,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
  {
    label: 'Instagram',
    value: '@carne_sfereima',
    href: 'https://www.instagram.com/carne_sfereima',
    target: '_blank',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
      </svg>
    ),
  },
  {
    label: 'Facebook',
    value: 'Carnes Fereima',
    href: 'https://www.facebook.com/CarnesFereima',
    target: '_blank',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    label: 'Dirección',
    value: 'Rancho los Aguirre #2\nSan Juan de los Lagos, Jalisco',
    href: MAPS_LINK,
    target: '_blank',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
      </svg>
    ),
  },
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const yLeft  = useTransform(scrollYProgress, [0, 1], [90, -90]);
  const yRight = useTransform(scrollYProgress, [0, 1], [-90, 90]);

  return (
    <Section id="contacto" ref={ref}>
      <BgGlow />
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (27).webp"
        alt=""
        style={{ left: '-40px', bottom: '-20px', rotate: '-10deg', y: yLeft }}
      />
      <SideImg
        src="/img/FOTOS FEREIMA/webp/image (28).webp"
        alt=""
        style={{ right: '-40px', top: '-20px', rotate: '10deg', y: yRight }}
      />

      <Inner>
        <Split>
          {/* ── Izquierda ── */}
          <Left>
            <EyebrowWrap
              initial={{ opacity: 0, x: -16 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Line />
              <Eyebrow>Contacto</Eyebrow>
            </EyebrowWrap>

            <Title
              initial={{ opacity: 0, y: 28 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
            >
              Hablemos<br />de <Accent>Negocios</Accent>
            </Title>

            <Sub
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease: EASE, delay: 0.2 }}
            >
              Estamos listos para atenderte y encontrar la mejor opción para ti.
            </Sub>

            <OLine
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.55, ease: EASE, delay: 0.35 }}
              style={{ originX: 0 }}
            />
          </Left>

          {/* ── Derecha ── */}
          <Right>
            {ROWS.map((row, i) => (
              <ContactRow
                key={row.label}
                as={row.href ? 'a' : 'div'}
                href={row.href || undefined}
                target={row.target}
                rel={row.target === '_blank' ? 'noopener noreferrer' : undefined}
                style={{ cursor: row.href ? 'pointer' : 'default' }}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, ease: EASE, delay: 0.15 + i * 0.08 }}
              >
                <RowIcon className="row-icon">{row.icon}</RowIcon>
                <RowContent>
                  <div className="label">{row.label}</div>
                  <div className="value">
                    {row.value.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < row.value.split('\n').length - 1 && <br />}</span>
                    ))}
                  </div>
                </RowContent>
                {row.href && (
                  <RowArrow className="row-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </RowArrow>
                )}
              </ContactRow>
            ))}
          </Right>
        </Split>

        <WAStrip
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: EASE, delay: 0.55 }}
        >
          <WAText>
            <div className="headline">¿Listo para hacer un pedido?</div>
            <div className="sub">Respuesta rápida, atención personalizada.</div>
          </WAText>
          <WABtn
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.124 1.532 5.862L0 24l6.337-1.501A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.584-.5-5.08-1.374l-.362-.214-3.763.891.936-3.667-.237-.375A9.954 9.954 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
            </svg>
            Escribir por WhatsApp
          </WABtn>
        </WAStrip>
      </Inner>
    </Section>
  );
}
